import cn from 'classnames';
import { useIntl } from 'react-intl';
import { Tooltip, TooltipInteractionEnum } from 'sonata-react-components';

import { useMultiFlightMap } from '../../../MultiFlightMap.context';

import type { ModifiedScreen } from '../../MapView/map.types';

import '../../Lists/lists.scss';
import './screensStackTooltip.scss';

type Props = {
  /** Screens sharing the same coordinates (more than one). */
  screens: Partial<ModifiedScreen>[];
  /** Diameter in px of the stack circle drawn by the map layer, so the hit area covers it. */
  size: number;
};

/** Transparent hit area over a stack of screens that lists them in a tooltip on click. */
function ScreensStackTooltip({ screens, size }: Props) {
  const { formatMessage } = useIntl();
  const { selectedItemId, setList, setMode, setSelectedItemId } = useMultiFlightMap();

  const handleSelect = (id: string) => {
    setSelectedItemId(id);
    setList('screens');
    setMode(null);
  };

  return (
    <Tooltip
      config={{
        component: (
          <ul className="screensStackTooltip__list">
            {screens.map(screen => {
              const id = String(screen.source_element_id);
              return (
                <li key={id}>
                  {/* Same look as a ScreensList row; the list icon replaces the pin because this selects the row there. */}
                  <button
                    className={cn(
                      'taptapListRowsListRow taptapListRowsListRow--withoutCheckbox mapList__listRowsListRow screensStackTooltip__item',
                      { 'taptapListRowsListRow--highlighted': id === selectedItemId }
                    )}
                    onClick={() => handleSelect(id)}
                    type="button"
                  >
                    <div className="mapList__listRowsListRowItemTitle">{screen.name || 'N/A'}</div>
                    <div className="mapList__listRowsListRowItemSubtitle">
                      <i className="fa-solid fa-list screensStackTooltip__listIcon" />
                      <span>{[screen.device_id || id, screen.adExchange_str].filter(Boolean).join(' | ')}</span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        ),
        interactions: [TooltipInteractionEnum.click],
        padding: 'var(--spacing-8)',
        placement: 'right',
        portal: true,
        strategy: 'fixed',
        type: 'contextualMenu'
      }}
    >
      <button
        aria-label={formatMessage(
          { defaultMessage: '{count} screens at this location', id: 'multiFlightListsMap.screensStack.ariaLabel' },
          { count: screens.length }
        )}
        className="screensStackTooltip__hitArea"
        style={{ height: size, width: size }}
        type="button"
      />
    </Tooltip>
  );
}

export default ScreensStackTooltip;
